import { z } from 'zod/v4';
import {
  ADDRESS_AREA_SCHEMA,
  AddressAreaId,
  IAddressArea,
} from './address-area.interfaces';
import {
  ILinkArea,
  LINK_AREA_SCHEMA,
  LinkAreaId,
} from './link-area.interfaces';
import {
  ILogoArea,
  LOGO_AREA_SCHEMA,
  LogoAreaId,
} from './logo-area.interfaces';
import {
  IPaymentArea,
  PAYMENT_AREA_SCHEMA,
  PaymentAreaId,
} from './payment-area.interfaces';
import { EEntityType, ERelationType } from './text-relation-type.enums';
import { ETextType } from './text/text.enums';
import { Brand } from '../branded-types';
import { ILink, LINK_SCHEMA, LinkId } from './link.interfaces';
import {
  INormalText,
  NORMAL_TEXT_SCHEMA,
  NormalTextId,
  TextId,
  TextLanguageValueId,
} from './text/text.interfaces';
import { LanguagePrefix } from './language.interfaces';
import {
  ACCESSIBLE_IMAGE_SCHEMA,
  AccessibleImageId,
  IAccessibleImage,
  IImage,
  ImageId,
} from './image.interfaces';
import { EFooterBlockType } from './footer-block-type.enums';

export type FooterId = Brand<string, 'footerId'>;
export type FooterBlockId = Brand<string, 'footerBlockId'>;

export const FOOTER_BLOCK_SCHEMA: z.ZodType<IFooterBlock> = z.object({
  id: z
    .uuid()
    .transform((s: string) => s as FooterBlockId)
    .optional(),
  type: z.enum(EFooterBlockType),
  orientation: z.enum(['vertical', 'horizontal']).optional(),
  isActive: z.boolean(),
  positionOrder: z.number().int().nonnegative(),
  headlines: z.array(NORMAL_TEXT_SCHEMA),
  texts: z.array(NORMAL_TEXT_SCHEMA),
  accessibleImages: z.array(ACCESSIBLE_IMAGE_SCHEMA),
  links: z.array(LINK_SCHEMA),
});

export interface IFooterBlock {
  id?: FooterBlockId;
  type: EFooterBlockType;
  orientation?: 'vertical' | 'horizontal';
  isActive: boolean;
  positionOrder: number;
  headlines: INormalText[];
  texts: INormalText[];
  accessibleImages: IAccessibleImage[];
  links: ILink[];
}

export interface IFooterPaymentProvider {
  providerId: string;
  visible: boolean;
  description: INormalText[];
  url: string;
}

const FOOTER_PAYMENT_PROVIDER_SCHEMA: z.ZodType<IFooterPaymentProvider> =
  z.object({
    providerId: z.uuid(),
    visible: z.boolean(),
    description: z.array(NORMAL_TEXT_SCHEMA),
    url: z.string(),
  });

export const FOOTER_SCHEMA = z.object({
  id: z
    .uuid()
    .transform((s: string) => s as FooterId)
    .optional(),

  backgroundColor: z.string(),

  appearance: z
    .object({
      showBackgroundImage: z.boolean(),
      backgroundImage: z.any().nullable().optional(),
      backgroundImageAlt: z.string().nullable().optional(),
      fontColor: z.string().nullable().optional(),
      backgroundColor: z.string().nullable().optional(),
      fontColorDark: z.string().nullable().optional(),
      backgroundColorDark: z.string().nullable().optional(),
    })
    .optional(),

  layout: z
    .object({
      blockOrder: z.array(z.string()),
      hiddenBlocks: z.array(z.string()),
      paymentProviders: z
        .record(z.string(), z.array(FOOTER_PAYMENT_PROVIDER_SCHEMA))
        .optional(),
    })
    .optional(),

  linkArea: LINK_AREA_SCHEMA,
  logoArea: LOGO_AREA_SCHEMA,
  addressArea: ADDRESS_AREA_SCHEMA,
  paymentArea: PAYMENT_AREA_SCHEMA,
  blocks: z.array(FOOTER_BLOCK_SCHEMA).optional(),
}) as z.ZodType<IFooter>;

export interface IFooter {
  id?: FooterId;
  backgroundColor: string;
  appearance?: {
    showBackgroundImage: boolean;
    backgroundImage?: IImage | null;
    backgroundImageAlt?: string | null;
    fontColor?: string | null;
    backgroundColor: string | null;
    fontColorDark?: string | null;
    backgroundColorDark?: string | null;
  };
  layout?: {
    blockOrder: string[];
    hiddenBlocks: string[];
    paymentProviders?: Record<string, IFooterPaymentProvider[]>;
  };
  logoArea: ILogoArea;
  addressArea: IAddressArea;
  paymentArea: IPaymentArea;
  linkArea: ILinkArea;
  blocks?: IFooterBlock[];
}

export const DEFAULT_FOOTER_BLOCKS: (IFooterBlock & { id: FooterBlockId })[] = [
  'eb58e568-8135-4b57-943c-f09a29a0fa06',
  'eb58e568-8135-4b57-943c-f09a29a0fa07',
  'eb58e568-8135-4b57-943c-f09a29a0fa08',
  'eb58e568-8135-4b57-943c-f09a29a0fa01',
  'eb58e568-8135-4b57-943c-f09a29a0fa02',
  'eb58e568-8135-4b57-943c-f09a29a0fa03',
  'eb58e568-8135-4b57-943c-f09a29a0fa04',
  'eb58e568-8135-4b57-943c-f09a29a0fa05',
].map((id, index) => ({
  id: id as FooterBlockId,
  type: EFooterBlockType.Default,
  orientation: 'vertical',
  isActive: true,
  positionOrder: index + 1,
  headlines: [],
  texts: [],
  accessibleImages: [],
  links: [],
}));

export const DEFAULT_FOOTER: IFooter = {
  id: 'a2c14e84-ddac-4e53-b44a-ca806c78b622' as FooterId,
  logoArea: {
    id: '89501067-5c74-48e8-a35f-f52bb7e21adf' as LogoAreaId,
    isActive: true,
    accessibleImages: [],
    headlines: [
      {
        id: '2b20bd08-d3f7-4dd8-8e0f-32af3b322976' as NormalTextId,
        textType: ETextType.Web,
        entityType: EEntityType.footerLogoArea,
        relationType: ERelationType.headline,
        text: {
          id: 'ebd72439-bfb8-4ef9-b528-2b5a3c711188' as TextId,
          values: [
            {
              id: 'f02ab02b-f8ef-4534-8b68-edb27fbeaf50' as TextLanguageValueId,
              languagePrefix: 'DE' as LanguagePrefix,
              value: 'Partner',
            },
          ],
        },
      },
    ],
    type: EFooterBlockType.Images,
    texts: [],
    links: [],
  },
  addressArea: {
    id: '6eae2655-45c9-4b30-a829-ad9fac8ccd22' as AddressAreaId,
    isActive: true,
    texts: [
      {
        id: '139536af-1e64-49e3-a891-4fa428b8970b' as NormalTextId,
        textType: ETextType.Web,
        entityType: EEntityType.footerAddressArea,
        relationType: ERelationType.text,
        text: {
          id: 'afa702e7-a263-483d-87d4-d5d4ef3c3aae' as TextId,
          values: [
            {
              id: '0c7bf97b-9d41-4f87-8184-e962b1b7a9a8' as TextLanguageValueId,
              languagePrefix: 'DE' as LanguagePrefix,
              value:
                'Dieser Ticketshop wird betrieben von:\nMusterunternehmen GmbH\nMusterstraße 29\n012378 Musterstadt',
            },
          ],
        },
      },
      {
        id: '047fc543-2bec-429c-a1b3-30a84c16c528' as NormalTextId,
        textType: ETextType.Web,
        relationType: ERelationType.headline,
        entityType: EEntityType.footerAddressArea,
        text: {
          id: '5f8fe36e-5232-4b78-b441-2a6a5c73d455' as TextId,
          values: [
            {
              id: 'be88ce37-4179-44fe-9631-e121731e897a' as TextLanguageValueId,
              value: 'Anschrift',
              languagePrefix: 'DE' as LanguagePrefix,
            },
          ],
        },
      },
    ],
    type: EFooterBlockType.Text,
    accessibleImages: [],
    links: [],
  },
  paymentArea: {
    id: 'ef27c046-0240-47de-93c3-07ae06706dbf' as PaymentAreaId,
    isActive: true,
    accessibleImages: [
      {
        id: '16839356-9b55-46ae-8869-122e2bbd8e20' as AccessibleImageId,
        imageAlts: [
          {
            id: '7117a86f-3c07-422f-a9cb-4d436ec07c42' as NormalTextId,
            textType: ETextType.Web,
            relationType: ERelationType.imageAlt,
            entityType: EEntityType.footerPaymentArea,
            text: {
              id: 'a71e72e6-c148-4eef-b0d3-138374d6b637' as TextId,
              values: [
                {
                  id: 'c4db7a9f-5c8e-49b9-873b-55da25c4ef95' as TextLanguageValueId,
                  value: 'LOGPAY',
                  languagePrefix: 'DE' as LanguagePrefix,
                },
              ],
            },
          },
        ],
        url: '',
        image: {
          id: 'b0e01472-7df0-4cb6-bb7c-2b62d26f634b' as ImageId,
          filename: 'LOGPAY',
          fileExtension: 'svg',
          createdAt: new Date('2025-08-29 07:33:26.030897 +00:00'),
        },
        positionOrder: 0,
      },
      {
        id: '96b86fb1-2e63-47a3-ba0d-aa21d1b9beaa' as AccessibleImageId,
        imageAlts: [
          {
            id: '3585d5e5-7e8c-4f11-9f2d-45bfeb2b5126' as NormalTextId,
            textType: ETextType.Web,
            relationType: ERelationType.imageAlt,
            entityType: EEntityType.footerPaymentArea,
            text: {
              id: '9ec489d8-99ee-473d-9d41-6927a760f384' as TextId,
              values: [
                {
                  id: '01867c42-2d83-4ee1-b99b-f11667dcfb2a' as TextLanguageValueId,
                  value: 'Mastercard',
                  languagePrefix: 'DE' as LanguagePrefix,
                },
              ],
            },
          },
        ],
        url: '',
        image: {
          id: 'c3f68420-5c64-4e78-9e56-11f8072049e2' as ImageId,
          filename: 'Mastercard',
          fileExtension: 'svg',
          createdAt: new Date('2025-08-29 07:33:26.030897 +00:00'),
        },
        positionOrder: 1,
      },
      {
        id: '2022e37f-37fe-4fa7-8625-f93ea374c478' as AccessibleImageId,
        imageAlts: [
          {
            id: '8a3a2a61-7132-4114-87ce-87fa83beff92' as NormalTextId,
            textType: ETextType.Web,
            relationType: ERelationType.imageAlt,
            entityType: EEntityType.footerPaymentArea,
            text: {
              id: 'd9b7ea57-9f44-4866-9ef0-eec1a49fbf1a' as TextId,
              values: [
                {
                  id: 'd6b70744-e2b2-4dca-9750-705b6dc19c8f' as TextLanguageValueId,
                  value: 'VISA',
                  languagePrefix: 'DE' as LanguagePrefix,
                },
              ],
            },
          },
        ],
        url: '',
        image: {
          id: 'd7a96e28-1b42-49cf-8927-14ec13c7bb90' as ImageId,
          filename: 'VISA',
          fileExtension: 'svg',
          createdAt: new Date('2025-08-29 07:33:26.030897 +00:00'),
        },
        positionOrder: 2,
      },
      {
        id: 'e9dfab12-6804-4114-82a8-06bc24dcfe27' as AccessibleImageId,
        imageAlts: [
          {
            id: 'fa40aeb7-1ce3-48aa-b541-e94ef34320e8' as NormalTextId,
            textType: ETextType.Web,
            relationType: ERelationType.imageAlt,
            entityType: EEntityType.footerPaymentArea,
            text: {
              id: '4ce150b0-379e-49b4-b9fa-f4632ea5dbba' as TextId,
              values: [
                {
                  id: '7d3be031-64d5-455b-bf72-e1d52044fe3c' as TextLanguageValueId,
                  value: 'PayPal',
                  languagePrefix: 'DE' as LanguagePrefix,
                },
              ],
            },
          },
        ],
        url: '',
        image: {
          id: 'e1c953a4-9213-48e0-94e8-89c0919241b7' as ImageId,
          filename: 'PayPal',
          fileExtension: 'svg',
          createdAt: new Date('2025-08-29 07:33:26.030897 +00:00'),
        },
        positionOrder: 3,
      },
    ],
    headlines: [
      {
        id: 'c5160456-c6bf-4e4e-8146-a7caae27b044' as NormalTextId,
        textType: ETextType.Web,
        relationType: ERelationType.headline,
        entityType: EEntityType.footerPaymentArea,
        text: {
          id: 'b7366f09-5f8b-4b46-a033-9257f38275fd' as TextId,
          values: [
            {
              id: '085c90e3-bb6c-4d20-b62d-65ba9bfb9954' as TextLanguageValueId,
              value: 'Mögliche Zahlungsarten',
              languagePrefix: 'DE' as LanguagePrefix,
            },
          ],
        },
      },
    ],
    type: EFooterBlockType.Payments,
    texts: [],
    links: [],
  },
  linkArea: {
    id: 'e673aaca-2636-4375-a312-3bfe99d742a4' as LinkAreaId,
    isActive: true,
    links: [
      {
        id: '54359e3c-da98-4ebd-8a89-ae71bac408bd' as LinkId,
        texts: [
          {
            id: 'eea2f95c-76b7-4e3c-9c1d-9461de202459' as NormalTextId,
            textType: ETextType.Web,
            entityType: EEntityType.footerLinkArea,
            relationType: ERelationType.url,
            text: {
              id: '1812af77-2af5-4ef7-9529-4fe1ff84a618' as TextId,
              values: [
                {
                  id: '435d18c8-e0a4-4f32-8b99-e9197b52e80e' as TextLanguageValueId,
                  languagePrefix: 'DE' as LanguagePrefix,
                  value: 'shop.amcon.de/impressum',
                },
              ],
            },
          },
          {
            id: '58406081-97d9-4e43-813f-efe9e72aa2bb' as NormalTextId,
            textType: ETextType.Web,
            entityType: EEntityType.footerLinkArea,
            relationType: ERelationType.text,
            text: {
              id: 'ebf83f08-f67b-4e15-80d0-4f86be577684' as TextId,
              values: [
                {
                  id: 'a43a0a3c-547d-4a6e-b11d-d7f92b1b0151' as TextLanguageValueId,
                  languagePrefix: 'DE' as LanguagePrefix,
                  value: 'Impressum',
                },
              ],
            },
          },
        ],
        positionOrder: 0,
      },
      {
        id: 'f923ddb3-2fdb-48e1-b760-a0148407381d' as LinkId,
        texts: [
          {
            id: 'a161b992-bd38-461d-880f-c0766b6ca558' as NormalTextId,
            textType: ETextType.Web,
            entityType: EEntityType.footerLinkArea,
            relationType: ERelationType.url,
            text: {
              id: '5bc9d35b-97a8-4e16-bcc4-a30f4039623f' as TextId,
              values: [
                {
                  id: '60818fff-3b1e-418a-9b30-a586d9ba327b' as TextLanguageValueId,
                  languagePrefix: 'DE' as LanguagePrefix,
                  value: 'shop.amcon.de/datenschutz',
                },
              ],
            },
          },
          {
            id: '5b453d0d-0109-49dd-a030-71eebc035f4c' as NormalTextId,
            textType: ETextType.Web,
            entityType: EEntityType.footerLinkArea,
            relationType: ERelationType.text,
            text: {
              id: '81946a73-719d-4234-88ce-ed1fa39ed753' as TextId,
              values: [
                {
                  id: '48040552-9d46-43d2-b913-dcafb8e0c59b' as TextLanguageValueId,
                  languagePrefix: 'DE' as LanguagePrefix,
                  value: 'Datenschutzerklärung',
                },
              ],
            },
          },
        ],
        positionOrder: 1,
      },
      {
        id: '8cc07abb-191f-4de0-8c66-25425b82dd2f' as LinkId,
        texts: [
          {
            id: '997cc2b4-52aa-4f2f-a9b8-a2ea06985485' as NormalTextId,
            textType: ETextType.Web,
            entityType: EEntityType.footerLinkArea,
            relationType: ERelationType.url,
            text: {
              id: '0bf78306-7408-4c31-b568-8ad0290c4c61' as TextId,
              values: [
                {
                  id: '7e5b250a-21fd-463b-8cd2-fcabbd100a74' as TextLanguageValueId,
                  languagePrefix: 'DE' as LanguagePrefix,
                  value: 'shop.amcon.de/kontakt',
                },
              ],
            },
          },
          {
            id: '28a3a64a-bc47-4bc1-b64f-1d809c0fcd75' as NormalTextId,
            textType: ETextType.Web,
            entityType: EEntityType.footerLinkArea,
            relationType: ERelationType.text,
            text: {
              id: '8590790e-73bc-48c7-b7b7-ecf04f4a927e' as TextId,
              values: [
                {
                  id: 'd54eb49c-fe84-467d-b4ae-704e667b0302' as TextLanguageValueId,
                  languagePrefix: 'DE' as LanguagePrefix,
                  value: 'Kontakt',
                },
              ],
            },
          },
        ],
        positionOrder: 2,
      },
      {
        id: '66c59fda-703c-4f12-9c65-802adc4108eb' as LinkId,
        texts: [
          {
            id: '2a00bb97-7950-4eb1-a31b-6af321ec3d5d' as NormalTextId,
            textType: ETextType.Web,
            entityType: EEntityType.footerLinkArea,
            relationType: ERelationType.url,
            text: {
              id: 'c10fd44d-9dc3-493d-913d-f413343d07e2' as TextId,
              values: [
                {
                  id: 'f6088fef-60da-49ae-afb9-63b44ea31e51' as TextLanguageValueId,
                  languagePrefix: 'DE' as LanguagePrefix,
                  value: 'www.verkehsunternehmen.de/faq',
                },
              ],
            },
          },
          {
            id: 'e815ae62-fcf3-4f73-9152-5f900a953572' as NormalTextId,
            textType: ETextType.Web,
            entityType: EEntityType.footerLinkArea,
            relationType: ERelationType.text,
            text: {
              id: '8a6c5147-585f-43bd-90c3-db3ea6198021' as TextId,
              values: [
                {
                  id: '1bed504b-5ea6-4c58-ba23-5502ba9f820a' as TextLanguageValueId,
                  languagePrefix: 'DE' as LanguagePrefix,
                  value: 'FAQ',
                },
              ],
            },
          },
        ],
        positionOrder: 3,
      },
    ],
    headlines: [
      {
        id: 'bf213580-b0b2-440b-ac99-2986911782d0' as NormalTextId,
        textType: ETextType.Web,
        relationType: ERelationType.headline,
        entityType: EEntityType.footerLinkArea,
        text: {
          id: 'c718e285-fa01-4ab2-8ed4-92fff492d48a' as TextId,
          values: [
            {
              id: 'b2349e4a-64f7-45dd-82c0-478dc025395a' as TextLanguageValueId,
              value: 'Links',
              languagePrefix: 'DE' as LanguagePrefix,
            },
          ],
        },
      },
    ],
    type: EFooterBlockType.Links,
    texts: [],
    accessibleImages: [],
  },
  backgroundColor: '#FFFFFF',
  appearance: {
    showBackgroundImage: false,
    backgroundImage: null,
    backgroundImageAlt: null,
    fontColor: '',
    backgroundColor: '',
  },
  layout: {
    blockOrder: [
      ...DEFAULT_FOOTER_BLOCKS.slice(0, 4).map(block => block.id),
      'ROW_BREAK',
      ...DEFAULT_FOOTER_BLOCKS.slice(4).map(block => block.id),
      'linkArea',
    ],
    hiddenBlocks: DEFAULT_FOOTER_BLOCKS.map(block => block.id),
  },
  blocks: DEFAULT_FOOTER_BLOCKS,
};
